import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Bell } from 'lucide-react';
import sparkLogo from '../assets/spark-logo.png';
import { useFeedData } from '../hooks/useFeedData';
import { PostCard } from '../components/Feed/PostCard';
import { useIdentity } from '../context/IdentityContext';
import { ShadowFeed } from './ShadowFeed';

export const WebAppHome = () => {
  const { identityMode } = useIdentity();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [showAppModal, setShowAppModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: 'Get the App', description: 'This feature is fully unlocked on the Spark mobile app. Download it now to get the full experience!' });
  const [showCustomRoomModal, setShowCustomRoomModal] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScrollDirection = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScrollDirection, { passive: true });
    return () => window.removeEventListener('scroll', handleScrollDirection);
  }, [lastScrollY]);

  const {
    activeTab,
    setActiveTab,
    posts,
    loading: feedLoading,
    loadingMore,
    loadMore,
    likedPosts,
    savedPosts,
    userProfiles,
    handleLikeUpdate,
    handleSaveUpdate,
    getFeedScroll,
    setFeedScroll
  } = useFeedData();

  // Restore scroll position on mount or tab change flawlessly
  React.useLayoutEffect(() => {
    window.scrollTo(0, getFeedScroll(activeTab));
  }, [activeTab, getFeedScroll]);

  // Handle scroll pagination and continuous scroll position saving
  useEffect(() => {
    const handleScroll = () => {
      setFeedScroll(activeTab, window.scrollY);
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 500) {
        loadMore();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab, loadMore, setFeedScroll]);

  if (identityMode === 'shadow') {
     return <ShadowFeed />;
  }

  return (
    <main className="w-full sm:w-[700px] min-h-screen border-r border-gray-800 flex flex-col pb-20 sm:pb-0 shrink-0">
      {/* Sticky Header */}
      <div className={`sticky top-0 bg-black/80 backdrop-blur-md z-40 border-b border-gray-800 flex flex-col pt-2 transition-transform duration-300 ${!isHeaderVisible ? '-translate-y-full' : 'translate-y-0'}`}>
         <div className="flex items-center justify-between px-4 sm:hidden pb-2">
            <img src={sparkLogo} alt="Spark" className="h-9 w-auto object-contain" />
            <button onClick={() => navigate('/notifications')} className="p-2 relative hover:bg-white/10 rounded-full transition-colors">
               <Bell size={22} className="text-gray-300" />
            </button>
         </div>
           <div className="flex w-full h-14">
              <button 
                onClick={() => setActiveTab('campus')}
                className="flex-1 hover:bg-white/5 transition-colors flex items-center justify-center relative group"
              >
                 <span className={`h-full flex items-center relative tracking-wide ${activeTab === 'campus' ? 'font-bold text-white' : 'font-medium text-gray-500'}`}>
                    Campus
                    {activeTab === 'campus' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#0BD9E5] rounded-t-full" />}
                 </span>
              </button>
              <button 
                onClick={() => setActiveTab('global')}
                className="flex-1 hover:bg-white/5 transition-colors flex items-center justify-center relative group"
              >
                 <span className={`h-full flex items-center relative tracking-wide ${activeTab === 'global' ? 'font-bold text-white' : 'font-medium text-gray-500'}`}>
                    Global
                    {activeTab === 'global' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#0BD9E5] rounded-t-full" />}
                 </span>
              </button>
              <button 
                onClick={() => setActiveTab('following')}
                className="flex-1 hover:bg-white/5 transition-colors flex items-center justify-center relative group"
              >
                 <span className={`h-full flex items-center relative tracking-wide ${activeTab === 'following' ? 'font-bold text-white' : 'font-medium text-gray-500'}`}>
                    Following
                    {activeTab === 'following' && <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#0BD9E5] rounded-t-full" />}
                 </span>
              </button>
           </div>
        </div>

        {/* Empty Feed Container */}
        <div className="flex-1 flex flex-col">
           {/* Temporary post box to look like X */}
           <div className="hidden sm:flex p-4 border-b border-gray-800 gap-4">
              <div className="w-10 h-10 rounded-full bg-gray-800 shrink-0 overflow-hidden border border-gray-700 cursor-pointer">
                 {userProfile?.photoURL ? <img src={userProfile.photoURL} alt="Avatar" className="w-full h-full object-cover" /> : <User className="w-full h-full p-2 text-gray-400" />}
              </div>
              <div className="flex-1">
                 <input type="text" placeholder="Drop some campus tea..." className="w-full bg-transparent text-[20px] outline-none placeholder-gray-500 mt-1 mb-4 text-white" disabled />
                 <div className="flex items-center justify-between border-t border-gray-800 pt-3">
                    <div className="flex gap-1 text-[#0BD9E5]">
                       <div className="w-9 h-9 rounded-full hover:bg-[#0BD9E5]/10 flex items-center justify-center cursor-pointer font-bold">🖼️</div>
                       <div className="w-9 h-9 rounded-full hover:bg-[#0BD9E5]/10 flex items-center justify-center cursor-pointer font-bold">📊</div>
                       <div className="w-9 h-9 rounded-full hover:bg-[#0BD9E5]/10 flex items-center justify-center cursor-pointer font-bold">😊</div>
                    </div>
                    <button className="bg-[#0BD9E5] text-black font-bold px-4 py-1.5 rounded-full opacity-50 cursor-not-allowed text-[15px]">Post</button>
                 </div>
              </div>
           </div>

           {/* Posts Feed */}
           <div className="flex-1 flex flex-col pb-12">
              {feedLoading && posts.length === 0 ? (
                 <div className="p-8 flex justify-center">
                    <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
                 </div>
              ) : posts.length > 0 ? (
                 <>
                    {posts.map(post => (
                       <PostCard 
                          key={post.id}
                          post={post}
                          userProfile={userProfiles[post.uid]}
                          isLiked={likedPosts.has(post.id)}
                          isSaved={savedPosts.has(post.id)}
                          onLikeUpdate={handleLikeUpdate}
                          onSaveUpdate={handleSaveUpdate}
                       />
                    ))}
                    {loadingMore && (
                       <div className="p-6 flex justify-center">
                          <div className="w-6 h-6 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
                       </div>
                    )}
                 </>
              ) : (
                 <div className="flex-1 flex flex-col items-center justify-center p-8 text-center mt-12">
                    <h2 className="text-[31px] font-extrabold mb-2 leading-tight">Welcome to your timeline</h2>
                    <p className="text-gray-500 mb-6 text-[15px]">When you follow people or when people post, you'll see them here.</p>
                 </div>
              )}
           </div>
        </div>
      </main>
  );
};

export default WebAppHome;
